<script setup>
import { computed, reactive, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';
import ContactsAPI from 'dashboard/api/contacts';
import API from 'dashboard/api/talkviaEmailCampaigns';
import { emailCampaignCopy } from '../emailCampaignCopy';
import {
  campaignPayload,
  campaignError,
  localDateTime,
} from '../emailCampaigns';

const emit = defineEmits(['saved']);
const copy = emailCampaignCopy;
const store = useStore();
const dialog = ref(null);
const busy = ref(false);
const search = ref('');
const matches = ref([]);
const searching = ref(false);
let searchController;
const form = reactive({});
const inboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(
    inbox => inbox.channel_type === 'Channel::Email'
  )
);
const labels = computed(() => store.getters['labels/getLabels']);
const canSave = computed(
  () =>
    form.title?.trim() &&
    form.subject?.trim() &&
    form.content?.trim() &&
    form.inbox_id &&
    (form.mode !== 'labels' || form.label_ids.length) &&
    (form.mode !== 'contacts' || form.contact_ids.length)
);

const open = (campaign = null) => {
  searchController?.abort();
  search.value = '';
  matches.value = [];
  Object.assign(form, {
    id: campaign?.id,
    title: campaign?.title || '',
    subject: campaign?.subject || '',
    content: campaign?.content || '',
    inbox_id: campaign?.inbox_id || inboxes.value[0]?.id || '',
    mode: campaign?.audience?.mode || 'all',
    label_ids: (campaign?.audience?.label_ids || []).map(Number),
    contact_ids: (campaign?.audience?.contact_ids || []).map(Number),
    rate_per_minute: campaign?.rate_per_minute || 6,
    scheduled_at: localDateTime(campaign?.scheduled_at),
  });
  dialog.value.open();
};

const findContacts = async () => {
  searchController?.abort();
  if (search.value.trim().length < 2) {
    matches.value = [];
    return;
  }
  const controller = new AbortController();
  searchController = controller;
  searching.value = true;
  try {
    const { data } = await ContactsAPI.search(
      search.value.trim(),
      1,
      'name',
      '',
      { signal: controller.signal }
    );
    matches.value = data.payload.filter(contact => contact.email);
  } catch (error) {
    if (!controller.signal.aborted) useAlert(campaignError(error));
  } finally {
    if (searchController === controller) searching.value = false;
  }
};

const save = async () => {
  if (busy.value || !canSave.value) return;
  busy.value = true;
  try {
    const payload = { campaign: campaignPayload(form) };
    const { data } = form.id
      ? await API.update(form.id, payload)
      : await API.create(payload);
    dialog.value.close();
    useAlert(
      'Rascunho salvo. Revise o e-mail e o público antes de confirmar o envio.'
    );
    emit('saved', data);
  } catch (error) {
    useAlert(campaignError(error));
  } finally {
    busy.value = false;
  }
};

const close = () => dialog.value?.close();
defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="copy.editorTitle"
    :description="copy.editorDescription"
    :confirm-button-label="copy.saveDraft"
    :disable-confirm-button="!canSave"
    :is-loading="busy"
    width="3xl"
    solid
    show-close-icon
    sticky-header-footer
    overflow-y-auto
    :close-on-click-outside="false"
    @confirm="save"
    @close="searchController?.abort()"
  >
    <div class="flex min-w-0 flex-col gap-5 pb-4">
      <Input v-model="form.title" :label="copy.name" />
      <label class="mb-0 flex flex-col gap-1 text-sm text-n-slate-12">
        <span>{{ copy.inbox }}</span>
        <select v-model="form.inbox_id" class="mb-0">
          <option value="" disabled>{{ copy.selectInbox }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ `${inbox.name}${copy.separator}${inbox.email || ''}` }}
          </option>
        </select>
      </label>
      <Input v-model="form.subject" :label="copy.subject" />
      <Editor
        v-model="form.content"
        :editor-key="`email-campaign-${form.id || 'new'}`"
        :label="copy.content"
        :placeholder="copy.contentPlaceholder"
        :max-length="100000"
        :enable-canned-responses="false"
        :enable-copilot-actions="false"
        channel-type="Channel::Email"
        medium="email"
      />
      <p class="m-0 text-xs text-n-slate-11">
        {{ copy.variables }}
      </p>
      <label class="mb-0 flex flex-col gap-1 text-sm text-n-slate-12">
        <span>{{ copy.audience }}</span>
        <select v-model="form.mode" class="mb-0">
          <option value="all">{{ copy.allContacts }}</option>
          <option value="labels">{{ copy.labeledContacts }}</option>
          <option value="contacts">{{ copy.specificContacts }}</option>
        </select>
      </label>
      <fieldset v-if="form.mode === 'labels'" class="m-0 border-0 p-0">
        <legend class="mb-2 text-sm text-n-slate-12">{{ copy.labels }}</legend>
        <p v-if="!labels.length" class="text-sm text-n-slate-11">
          {{ copy.noLabels }}
        </p>
        <div class="flex flex-wrap gap-3">
          <label
            v-for="label in labels"
            :key="label.id"
            class="mb-0 flex items-center gap-2 text-sm"
          >
            <input
              v-model="form.label_ids"
              type="checkbox"
              :value="label.id"
              class="mb-0"
            />
            {{ label.title }}
          </label>
        </div>
      </fieldset>
      <div v-if="form.mode === 'contacts'" class="flex flex-col gap-3">
        <div class="flex flex-wrap items-end gap-2">
          <Input
            v-model="search"
            :label="copy.searchLabel"
            class="min-w-0 flex-1"
            @keydown.enter.prevent="findContacts"
          />
          <Button
            type="button"
            :label="copy.search"
            variant="faded"
            color="slate"
            :is-loading="searching"
            @click="findContacts"
          />
        </div>
        <p class="m-0 text-sm text-n-slate-11">
          {{ `${form.contact_ids.length} ${copy.selected}` }}
          <button
            type="button"
            class="text-n-brand"
            @click="form.contact_ids = []"
          >
            {{ copy.clearSelection }}
          </button>
        </p>
        <p
          v-if="search.length >= 2 && !searching && !matches.length"
          class="m-0 text-sm text-n-slate-11"
        >
          {{ copy.searchHint }}
        </p>
        <label
          v-for="contact in matches"
          :key="contact.id"
          class="mb-0 flex min-w-0 items-center gap-2 text-sm"
        >
          <input
            v-model="form.contact_ids"
            type="checkbox"
            :value="contact.id"
            class="mb-0"
          />
          <span class="min-w-0 break-all">{{
            `${contact.name}${copy.separator}${contact.email}`
          }}</span>
        </label>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          v-model="form.scheduled_at"
          :label="copy.schedule"
          with-time
          :min="localDateTime(new Date(Date.now() + 60000))"
        />
        <Input
          v-model="form.rate_per_minute"
          type="number"
          :label="copy.rate"
          :min="1"
          :max="60"
        />
      </div>
      <p class="m-0 text-xs text-n-slate-11">{{ copy.sendingHint }}</p>
    </div>
  </Dialog>
</template>
